/**
 * DataArtifacts.jsx - Composants d'affichage des données dans le panneau droit du Cockpit
 * Ces composants sont utilisés par CockpitAssistantUnified pour afficher les vraies données
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, FileText, Receipt, Euro, Calendar, Clock, Phone, Mail, MapPin,
  Building, ChevronRight, Plus, Search, Filter, MoreHorizontal, Eye,
  Edit3, Trash2, Download, Send, CheckCircle, XCircle, AlertCircle,
  TrendingUp, Package, Wrench, MessageCircle, Heart, Star, ShoppingBag,
  Bookmark, User, Bell, Home, Target, Loader2, RefreshCw, ExternalLink,
  ArrowLeft, History, Info, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT GÉNÉRIQUE - Liste vide
// ═══════════════════════════════════════════════════════════════════════════════
export function EmptyState({ icon: Icon, title, description, actionLabel, onAction }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
      <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
        <Icon size={28} className="text-gray-400" />
      </div>
      <h3 className="text-lg font-semibold text-gray-800 mb-2">{title}</h3>
      <p className="text-gray-500 text-sm mb-6 max-w-sm">{description}</p>
      {actionLabel && onAction && (
        <button
          onClick={onAction}
          className="flex items-center gap-2 px-4 py-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-medium rounded-xl transition-colors"
        >
          <Plus size={18} />
          {actionLabel}
        </button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT GÉNÉRIQUE - Header de liste
// ═══════════════════════════════════════════════════════════════════════════════
export function ListHeader({ title, count, onRefresh, onAdd, addLabel, isLoading }) {
  return (
    <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50/50">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-bold text-[#1E3A5F]">{title}</h2>
        {count !== undefined && (
          <span className="px-2.5 py-0.5 bg-[#F97316]/10 text-[#F97316] text-sm font-medium rounded-full">
            {count}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {onRefresh && (
          <button 
            onClick={onRefresh}
            disabled={isLoading}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <RefreshCw size={18} className={`text-gray-500 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        )}
        {onAdd && (
          <button
            onClick={onAdd}
            className="flex items-center gap-2 px-3 py-2 bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-medium rounded-xl transition-colors"
          >
            <Plus size={16} />
            {addLabel || 'Ajouter'}
          </button>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT DÉTAIL CLIENT/PROSPECT/CONTACT avec Historique IA
// ═══════════════════════════════════════════════════════════════════════════════
export function EntityDetailView({ 
  entity, 
  entityType = 'client', // 'client', 'prospect', 'contact'
  onBack, 
  onStartConversation,
  onOpenConversation 
}) {
  const [activeTab, setActiveTab] = useState('info');
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  
  const entityId = entity?.id || entity?._id;
  
  // Charger l'historique des conversations
  useEffect(() => {
    if (activeTab === 'historique' && entityId) {
      fetchSessions();
    }
  }, [activeTab, entityId]);
  
  const fetchSessions = async () => {
    if (!entityId) return;
    setLoadingSessions(true);
    try {
      let endpoint = '';
      if (entityType === 'client') {
        endpoint = `${API_URL}/api/ai/sessions/by-client/${entityId}`;
      } else if (entityType === 'prospect') {
        endpoint = `${API_URL}/api/ai/sessions/by-prospect/${entityId}`;
      } else if (entityType === 'contact') {
        endpoint = `${API_URL}/api/ai/sessions/by-contact/${entityId}`;
      }
      
      const response = await fetch(endpoint);
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      }
    } catch (error) {
      console.error('Erreur chargement historique:', error);
    } finally {
      setLoadingSessions(false);
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };
  
  const getEntityLabel = () => {
    switch (entityType) {
      case 'prospect': return 'Prospect';
      case 'contact': return 'Contact';
      default: return 'Client';
    }
  };
  
  const getEntityColor = () => {
    switch (entityType) {
      case 'prospect': return '#1E3A5F';
      case 'contact': return '#8B5CF6';
      default: return '#F97316';
    }
  };
  
  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header avec bouton retour */}
      <div 
        className="flex items-center gap-3 px-4 py-3 border-b border-gray-200"
        style={{ backgroundColor: `${getEntityColor()}10` }}
      >
        <button 
          onClick={onBack}
          className="p-2 hover:bg-white/50 rounded-lg transition-colors"
          data-testid="entity-detail-back-btn"
        >
          <ArrowLeft size={20} style={{ color: getEntityColor() }} />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium" style={{ color: getEntityColor() }}>
            {getEntityLabel()}
          </p>
          <p className="font-bold text-[#1E3A5F] truncate">
            {entity?.name || entity?.company || `${entity?.first_name || ''} ${entity?.last_name || ''}`.trim() || 'Sans nom'}
          </p>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('info')}
          className={`flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
            activeTab === 'info' 
              ? 'text-[#F97316] border-b-2 border-[#F97316]' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
          data-testid="tab-info"
        >
          <Info size={16} />
          Informations
        </button>
        <button
          onClick={() => setActiveTab('historique')}
          className={`flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
            activeTab === 'historique' 
              ? 'text-[#F97316] border-b-2 border-[#F97316]' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
          data-testid="tab-historique"
        >
          <History size={16} />
          Historique IA
        </button>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <AnimatePresence mode="wait">
          {activeTab === 'info' ? (
            <motion.div
              key="info"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="p-4 space-y-4"
            >
              {/* Avatar & Nom */}
              <div className="flex items-center gap-4">
                <div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${getEntityColor()}20` }}
                >
                  <span className="text-2xl font-bold" style={{ color: getEntityColor() }}>
                    {(entity?.name || entity?.company || entity?.first_name || 'X').charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h3 className="font-bold text-lg text-[#1E3A5F]">
                    {entity?.name || `${entity?.first_name || ''} ${entity?.last_name || ''}`.trim() || 'Sans nom'}
                  </h3>
                  {entity?.company && (
                    <p className="text-sm text-gray-600 flex items-center gap-1">
                      <Building size={14} />
                      {entity.company}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Infos de contact */}
              <div className="space-y-3 bg-gray-50 rounded-xl p-4">
                {entity?.email && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
                      <Mail size={16} className="text-gray-500" />
                    </div>
                    <span className="text-sm text-gray-700">{entity.email}</span>
                  </div>
                )}
                {entity?.phone && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
                      <Phone size={16} className="text-gray-500" />
                    </div>
                    <span className="text-sm text-gray-700">{entity.phone}</span>
                  </div>
                )}
                {entity?.address && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
                      <MapPin size={16} className="text-gray-500" />
                    </div>
                    <span className="text-sm text-gray-700">{entity.address}</span>
                  </div>
                )}
                {!entity?.email && !entity?.phone && !entity?.address && (
                  <p className="text-sm text-gray-400 text-center py-2">Aucune information de contact</p>
                )}
              </div>
              
              {/* Statut (pour prospects) */}
              {entityType === 'prospect' && entity?.status && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 mb-2">Statut</p>
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                    entity.status === 'won' ? 'bg-green-100 text-green-700' :
                    entity.status === 'lost' ? 'bg-red-100 text-red-700' :
                    entity.status === 'qualified' ? 'bg-purple-100 text-purple-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {entity.status === 'new' ? 'Nouveau' :
                     entity.status === 'contacted' ? 'Contacté' :
                     entity.status === 'qualified' ? 'Qualifié' :
                     entity.status === 'negotiating' ? 'En négociation' :
                     entity.status === 'won' ? 'Gagné' :
                     entity.status === 'lost' ? 'Perdu' : entity.status}
                  </span>
                </div>
              )}
              
              {/* Bouton nouvelle conversation */}
              <button
                onClick={() => onStartConversation?.(entity)}
                className="w-full py-3 px-4 bg-[#F97316] hover:bg-[#EA580C] text-white font-medium rounded-xl flex items-center justify-center gap-2 transition-colors"
                data-testid="start-conversation-btn"
              >
                <Sparkles size={18} />
                Nouvelle conversation IA
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="historique"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              className="p-4"
            >
              {loadingSessions ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="animate-spin text-[#F97316]" size={24} />
                </div>
              ) : sessions.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-14 h-14 rounded-xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
                    <MessageCircle size={24} className="text-gray-400" />
                  </div>
                  <p className="text-gray-500 text-sm mb-4">
                    Aucune conversation liée à ce {getEntityLabel().toLowerCase()}
                  </p>
                  <button
                    onClick={() => onStartConversation?.(entity)}
                    className="text-[#F97316] text-sm font-medium hover:underline"
                  >
                    Démarrer une conversation
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 mb-3">
                    {sessions.length} conversation{sessions.length > 1 ? 's' : ''} liée{sessions.length > 1 ? 's' : ''}
                  </p>
                  {sessions.map((session, idx) => (
                    <motion.div
                      key={session.session_id || idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="p-3 bg-gray-50 hover:bg-gray-100 rounded-xl cursor-pointer transition-colors"
                      onClick={() => onOpenConversation?.(session.session_id)}
                      data-testid={`session-item-${idx}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-[#F97316]/10 flex items-center justify-center flex-shrink-0">
                          <MessageCircle size={16} className="text-[#F97316]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#1E3A5F] text-sm truncate">
                            {session.title || 'Conversation'}
                          </p>
                          {session.last_message && (
                            <p className="text-xs text-gray-500 truncate mt-0.5">
                              {session.last_message}
                            </p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            {formatDate(session.updated_at)}
                          </p>
                        </div>
                        <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// DEVIS - Liste des devis
// ═══════════════════════════════════════════════════════════════════════════════
export function ArtifactDevisList({ data, onSelectItem, onRefresh }) {
  const items = Array.isArray(data) ? data : data?.items || data?.devis || [];
  const [isLoading, setIsLoading] = useState(false);

  const getStatusBadge = (status) => {
    const statusMap = {
      'draft': { label: 'Brouillon', color: 'bg-gray-100 text-gray-700' },
      'pending': { label: 'En attente', color: 'bg-yellow-100 text-yellow-700' },
      'sent': { label: 'Envoyé', color: 'bg-blue-100 text-blue-700' },
      'accepted': { label: 'Accepté', color: 'bg-green-100 text-green-700' },
      'refused': { label: 'Refusé', color: 'bg-red-100 text-red-700' },
      'expired': { label: 'Expiré', color: 'bg-gray-100 text-gray-500' },
    };
    return statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-600' };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatMoney = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  if (!items || items.length === 0) {
    return (
      <EmptyState
        icon={Receipt}
        title="Aucun devis"
        description="Tu n'as pas encore créé de devis. Demande-moi d'en créer un !"
        actionLabel="Créer un devis"
        onAction={() => toast.info('Demande-moi de créer un devis dans le chat !')}
      />
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ListHeader 
        title="Mes Devis" 
        count={items.length} 
        onRefresh={onRefresh}
        isLoading={isLoading}
      />
      <div className="flex-1 overflow-y-auto scrollbar-hide divide-y divide-gray-100">
        {items.map((devis, idx) => {
          const status = getStatusBadge(devis.status);
          return (
            <motion.div
              key={devis.id || devis._id || idx}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => onSelectItem?.(devis)}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-[#1E3A5F] truncate">
                      {devis.number || devis.reference || `Devis #${idx + 1}`}
                    </span>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${status.color}`}>
                      {status.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 truncate">
                    {devis.client_name || devis.client?.name || 'Client non défini'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {formatDate(devis.created_at || devis.date)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-[#1E3A5F]">
                    {formatMoney(devis.total_ttc || devis.total || devis.amount)}
                  </p>
                  <p className="text-xs text-gray-400">TTC</p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// FACTURES - Liste des factures
// ═══════════════════════════════════════════════════════════════════════════════
export function ArtifactFacturesList({ data, onSelectItem, onRefresh }) {
  const items = Array.isArray(data) ? data : data?.items || data?.factures || [];

  const getStatusBadge = (status, isPaid) => {
    if (isPaid) return { label: 'Payée', color: 'bg-green-100 text-green-700' };
    const statusMap = {
      'draft': { label: 'Brouillon', color: 'bg-gray-100 text-gray-700' },
      'sent': { label: 'Envoyée', color: 'bg-blue-100 text-blue-700' },
      'overdue': { label: 'En retard', color: 'bg-red-100 text-red-700' },
      'paid': { label: 'Payée', color: 'bg-green-100 text-green-700' },
    };
    return statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-600' };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatMoney = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  if (!items || items.length === 0) {
    return (
      <EmptyState
        icon={Receipt}
        title="Aucune facture"
        description="Tu n'as pas encore de factures. Elles seront créées à partir de tes devis acceptés."
      />
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ListHeader 
        title="Mes Factures" 
        count={items.length}
        onRefresh={onRefresh}
      />
      <div className="flex-1 overflow-y-auto scrollbar-hide divide-y divide-gray-100">
        {items.map((facture, idx) => {
          const status = getStatusBadge(facture.status, facture.is_paid);
          return (
            <motion.div
              key={facture.id || facture._id || idx}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => onSelectItem?.(facture)}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-[#1E3A5F] truncate">
                      {facture.number || facture.reference || `Facture #${idx + 1}`}
                    </span>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${status.color}`}>
                      {status.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 truncate">
                    {facture.client_name || facture.client?.name || 'Client'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Échéance : {formatDate(facture.due_date || facture.echeance)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-[#1E3A5F]">
                    {formatMoney(facture.total_ttc || facture.total || facture.amount)}
                  </p>
                  <p className="text-xs text-gray-400">TTC</p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CLIENTS - Liste des clients avec vue détaillée
// ═══════════════════════════════════════════════════════════════════════════════
export function ArtifactClientsList({ 
  data, 
  onSelectItem, 
  onRefresh,
  onStartConversation,
  onOpenConversation 
}) {
  const items = Array.isArray(data) ? data : data?.items || data?.clients || [];
  const [selectedClient, setSelectedClient] = useState(null);

  // Si un client est sélectionné, afficher la vue détaillée
  if (selectedClient) {
    return (
      <EntityDetailView
        entity={selectedClient}
        entityType="client"
        onBack={() => setSelectedClient(null)}
        onStartConversation={(client) => {
          onStartConversation?.(client, 'client');
        }}
        onOpenConversation={onOpenConversation}
      />
    );
  }

  if (!items || items.length === 0) {
    return (
      <EmptyState
        icon={Users}
        title="Aucun client"
        description="Tu n'as pas encore de clients. Ils seront ajoutés automatiquement lors de la création de devis."
      />
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ListHeader 
        title="Mes Clients" 
        count={items.length}
        onRefresh={onRefresh}
      />
      <div className="flex-1 overflow-y-auto scrollbar-hide divide-y divide-gray-100">
        {items.map((client, idx) => (
          <motion.div
            key={client.id || client._id || idx}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
            className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
            onClick={() => setSelectedClient(client)}
            data-testid={`client-item-${idx}`}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#F97316]/10 flex items-center justify-center flex-shrink-0">
                <span className="text-lg font-bold text-[#F97316]">
                  {(client.name || client.first_name || 'C').charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#1E3A5F] truncate">
                  {client.name || `${client.first_name || ''} ${client.last_name || ''}`.trim() || 'Client'}
                </p>
                {client.company && (
                  <p className="text-sm text-gray-600 truncate flex items-center gap-1">
                    <Building size={14} />
                    {client.company}
                  </p>
                )}
                <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                  {client.email && (
                    <span className="flex items-center gap-1">
                      <Mail size={12} />
                      {client.email}
                    </span>
                  )}
                  {client.phone && (
                    <span className="flex items-center gap-1">
                      <Phone size={12} />
                      {client.phone}
                    </span>
                  )}
                </div>
              </div>
              <ChevronRight size={20} className="text-gray-400" />
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PROSPECTS - Liste des prospects
// ═══════════════════════════════════════════════════════════════════════════════
export function ArtifactProspectsList({ 
  data, 
  onSelectItem, 
  onRefresh,
  onStartConversation,
  onOpenConversation 
}) {
  const items = Array.isArray(data) ? data : data?.items || data?.prospects || [];
  const [selectedProspect, setSelectedProspect] = useState(null);

  const getStatusBadge = (status) => {
    const statusMap = {
      'new': { label: 'Nouveau', color: 'bg-blue-100 text-blue-700' },
      'contacted': { label: 'Contacté', color: 'bg-yellow-100 text-yellow-700' },
      'qualified': { label: 'Qualifié', color: 'bg-purple-100 text-purple-700' },
      'negotiating': { label: 'Négociation', color: 'bg-orange-100 text-orange-700' },
      'won': { label: 'Gagné', color: 'bg-green-100 text-green-700' },
      'lost': { label: 'Perdu', color: 'bg-red-100 text-red-700' },
    };
    return statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-600' };
  };

  // Si un prospect est sélectionné, afficher la vue détaillée
  if (selectedProspect) {
    return (
      <EntityDetailView
        entity={selectedProspect}
        entityType="prospect"
        onBack={() => setSelectedProspect(null)}
        onStartConversation={(prospect) => {
          onStartConversation?.(prospect, 'prospect');
        }}
        onOpenConversation={onOpenConversation}
      />
    );
  }

  if (!items || items.length === 0) {
    return (
      <EmptyState
        icon={Target}
        title="Aucun prospect"
        description="Tu n'as pas encore de prospects. Ajoute tes premiers contacts commerciaux."
        actionLabel="Ajouter un prospect"
        onAction={() => toast.info('Demande-moi d\'ajouter un prospect dans le chat !')}
      />
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ListHeader 
        title="Mes Prospects" 
        count={items.length}
        onRefresh={onRefresh}
      />
      <div className="flex-1 overflow-y-auto scrollbar-hide divide-y divide-gray-100">
        {items.map((prospect, idx) => {
          const status = getStatusBadge(prospect.status);
          return (
            <motion.div
              key={prospect.id || prospect._id || idx}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => setSelectedProspect(prospect)}
              data-testid={`prospect-item-${idx}`}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#1E3A5F]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-lg font-bold text-[#1E3A5F]">
                    {(prospect.name || prospect.company || 'P').charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold text-[#1E3A5F] truncate">
                      {prospect.name || prospect.company || 'Prospect'}
                    </p>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${status.color}`}>
                      {status.label}
                    </span>
                  </div>
                  {prospect.company && prospect.name && (
                    <p className="text-sm text-gray-600 truncate">{prospect.company}</p>
                  )}
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                    {prospect.email && (
                      <span className="flex items-center gap-1">
                        <Mail size={12} />
                        {prospect.email}
                      </span>
                    )}
                    {prospect.estimated_value && (
                      <span className="flex items-center gap-1 text-green-600 font-medium">
                        <Euro size={12} />
                        {prospect.estimated_value.toLocaleString('fr-FR')}
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight size={20} className="text-gray-400" />
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TÂCHES - Liste des tâches
// ═══════════════════════════════════════════════════════════════════════════════
export function ArtifactTasksList({ data, onSelectItem, onRefresh }) {
  const items = Array.isArray(data) ? data : data?.items || data?.tasks || [];

  const getPriorityBadge = (priority) => {
    const priorityMap = {
      'high': { label: 'Urgent', color: 'bg-red-100 text-red-700' },
      'medium': { label: 'Normal', color: 'bg-yellow-100 text-yellow-700' },
      'low': { label: 'Basse', color: 'bg-gray-100 text-gray-600' },
    };
    return priorityMap[priority] || { label: priority, color: 'bg-gray-100 text-gray-600' };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) return 'Aujourd\'hui';
    if (date.toDateString() === tomorrow.toDateString()) return 'Demain';
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  if (!items || items.length === 0) {
    return (
      <EmptyState
        icon={FileText}
        title="Aucune tâche"
        description="Tu n'as pas de tâches en cours. Crée-en une pour organiser ton travail !"
        actionLabel="Créer une tâche"
        onAction={() => toast.info('Demande-moi de créer une tâche dans le chat !')}
      />
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ListHeader 
        title="Mes Tâches" 
        count={items.length}
        onRefresh={onRefresh}
      />
      <div className="flex-1 overflow-y-auto scrollbar-hide divide-y divide-gray-100">
        {items.map((task, idx) => {
          const priority = getPriorityBadge(task.priority);
          const dueDate = formatDate(task.due_date);
          const isOverdue = task.due_date && new Date(task.due_date) < new Date() && !task.completed;
          
          return (
            <motion.div
              key={task.id || task._id || idx}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${task.completed ? 'opacity-60' : ''}`}
              onClick={() => onSelectItem?.(task)}
            >
              <div className="flex items-start gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  task.completed ? 'bg-green-500 border-green-500' : 'border-gray-300'
                }`}>
                  {task.completed && <CheckCircle size={14} className="text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`font-medium text-[#1E3A5F] ${task.completed ? 'line-through' : ''}`}>
                    {task.title || task.name || 'Tâche sans titre'}
                  </p>
                  {task.description && (
                    <p className="text-sm text-gray-500 truncate mt-0.5">{task.description}</p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    {priority && (
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${priority.color}`}>
                        {priority.label}
                      </span>
                    )}
                    {dueDate && (
                      <span className={`text-xs flex items-center gap-1 ${isOverdue ? 'text-red-500' : 'text-gray-400'}`}>
                        <Clock size={12} />
                        {dueDate}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ÉQUIPE - Liste des membres
// ═══════════════════════════════════════════════════════════════════════════════
export function ArtifactTeamList({ data, onSelectItem, onRefresh }) {
  const items = Array.isArray(data) ? data : data?.items || data?.members || [];

  const getRoleBadge = (role) => {
    const roleMap = {
      'admin': { label: 'Admin', color: 'bg-purple-100 text-purple-700' },
      'manager': { label: 'Manager', color: 'bg-blue-100 text-blue-700' },
      'member': { label: 'Membre', color: 'bg-gray-100 text-gray-600' },
      'viewer': { label: 'Lecteur', color: 'bg-gray-100 text-gray-500' },
    };
    return roleMap[role] || { label: role, color: 'bg-gray-100 text-gray-600' };
  };

  if (!items || items.length === 0) {
    return (
      <EmptyState
        icon={Users}
        title="Aucun membre"
        description="Tu es seul pour le moment. Invite des collaborateurs pour travailler ensemble !"
        actionLabel="Inviter un membre"
        onAction={() => toast.info('Demande-moi d\'inviter un membre dans le chat !')}
      />
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ListHeader 
        title="Mon Équipe" 
        count={items.length}
        onRefresh={onRefresh}
        addLabel="Inviter"
      />
      <div className="flex-1 overflow-y-auto scrollbar-hide divide-y divide-gray-100">
        {items.map((member, idx) => {
          const role = getRoleBadge(member.role);
          return (
            <motion.div
              key={member.id || member._id || idx}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => onSelectItem?.(member)}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#1E3A5F] to-[#2D5A8F] flex items-center justify-center flex-shrink-0">
                  {member.avatar ? (
                    <img src={member.avatar} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <span className="text-lg font-bold text-white">
                      {(member.name || member.first_name || 'M').charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-[#1E3A5F] truncate">
                      {member.name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Membre'}
                    </p>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${role.color}`}>
                      {role.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 truncate">{member.email}</p>
                </div>
                <div className={`w-3 h-3 rounded-full ${member.is_online ? 'bg-green-500' : 'bg-gray-300'}`} />
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// AGENDA / ÉVÉNEMENTS - Liste des événements
// ═══════════════════════════════════════════════════════════════════════════════
export function ArtifactEventsList({ data, onSelectItem, onRefresh }) {
  const items = Array.isArray(data) ? data : data?.items || data?.events || [];

  const formatEventTime = (start, end) => {
    if (!start) return '';
    const startDate = new Date(start);
    const options = { hour: '2-digit', minute: '2-digit' };
    let str = startDate.toLocaleTimeString('fr-FR', options);
    if (end) {
      const endDate = new Date(end);
      str += ` - ${endDate.toLocaleTimeString('fr-FR', options)}`;
    }
    return str;
  };

  const formatEventDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) return 'Aujourd\'hui';
    if (date.toDateString() === tomorrow.toDateString()) return 'Demain';
    return date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  if (!items || items.length === 0) {
    return (
      <EmptyState
        icon={Calendar}
        title="Aucun événement"
        description="Ton agenda est vide. Planifie tes rendez-vous et réunions !"
        actionLabel="Créer un événement"
        onAction={() => toast.info('Demande-moi de créer un événement dans le chat !')}
      />
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ListHeader 
        title="Mon Agenda" 
        count={items.length}
        onRefresh={onRefresh}
      />
      <div className="flex-1 overflow-y-auto scrollbar-hide divide-y divide-gray-100">
        {items.map((event, idx) => (
          <motion.div
            key={event.id || event._id || idx}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
            className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
            onClick={() => onSelectItem?.(event)}
          >
            <div className="flex items-start gap-3">
              <div className={`w-1.5 h-full min-h-[60px] rounded-full ${event.color || 'bg-[#F97316]'}`} />
              <div className="flex-1">
                <p className="font-semibold text-[#1E3A5F]">{event.title || event.summary || 'Événement'}</p>
                <p className="text-sm text-gray-500 mt-1">
                  {formatEventDate(event.start || event.start_time)}
                </p>
                <p className="text-sm text-[#F97316] font-medium">
                  {formatEventTime(event.start || event.start_time, event.end || event.end_time)}
                </p>
                {event.location && (
                  <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                    <MapPin size={12} />
                    {event.location}
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MESSAGES - Liste des conversations
// ═══════════════════════════════════════════════════════════════════════════════
export function ArtifactMessagesList({ data, onSelectItem, onRefresh }) {
  const items = Array.isArray(data) ? data : data?.items || data?.conversations || data?.messages || [];

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'À l\'instant';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} h`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  if (!items || items.length === 0) {
    return (
      <EmptyState
        icon={MessageCircle}
        title="Aucun message"
        description="Tu n'as pas encore de conversations. Commence à discuter avec tes contacts !"
      />
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ListHeader 
        title="Messages" 
        count={items.length}
        onRefresh={onRefresh}
      />
      <div className="flex-1 overflow-y-auto scrollbar-hide divide-y divide-gray-100">
        {items.map((conv, idx) => (
          <motion.div
            key={conv.id || conv._id || idx}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
            className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${conv.unread ? 'bg-[#FFF8F4]' : ''}`}
            onClick={() => onSelectItem?.(conv)}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#F97316] to-[#EA580C] flex items-center justify-center flex-shrink-0 relative">
                <span className="text-lg font-bold text-white">
                  {(conv.participant_name || conv.other_user?.name || conv.name || 'U').charAt(0).toUpperCase()}
                </span>
                {conv.unread && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                    <span className="text-[10px] text-white font-bold">{conv.unread_count || ''}</span>
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className={`font-semibold text-[#1E3A5F] truncate ${conv.unread ? 'text-[#1E3A5F]' : ''}`}>
                    {conv.participant_name || conv.other_user?.name || conv.name || 'Conversation'}
                  </p>
                  <span className="text-xs text-gray-400">
                    {formatDate(conv.last_message_at || conv.updated_at)}
                  </span>
                </div>
                <p className={`text-sm truncate mt-0.5 ${conv.unread ? 'text-gray-800 font-medium' : 'text-gray-500'}`}>
                  {conv.last_message || conv.preview || 'Aucun message'}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CHANTIERS - Liste des chantiers
// ═══════════════════════════════════════════════════════════════════════════════
export function ArtifactChantiersList({ data, onSelectItem, onRefresh }) {
  const items = Array.isArray(data) ? data : data?.items || data?.chantiers || [];

  const getStatusBadge = (status) => {
    const statusMap = {
      'planning': { label: 'Planification', color: 'bg-blue-100 text-blue-700' },
      'in_progress': { label: 'En cours', color: 'bg-green-100 text-green-700' },
      'on_hold': { label: 'En pause', color: 'bg-yellow-100 text-yellow-700' },
      'completed': { label: 'Terminé', color: 'bg-gray-100 text-gray-600' },
      'cancelled': { label: 'Annulé', color: 'bg-red-100 text-red-700' },
    };
    return statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-600' };
  };

  if (!items || items.length === 0) {
    return (
      <EmptyState
        icon={Wrench}
        title="Aucun chantier"
        description="Tu n'as pas encore de chantiers en cours."
      />
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ListHeader 
        title="Mes Chantiers" 
        count={items.length}
        onRefresh={onRefresh}
      />
      <div className="flex-1 overflow-y-auto scrollbar-hide divide-y divide-gray-100">
        {items.map((chantier, idx) => {
          const status = getStatusBadge(chantier.status);
          return (
            <motion.div
              key={chantier.id || chantier._id || idx}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => onSelectItem?.(chantier)}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#1E3A5F]/10 flex items-center justify-center flex-shrink-0">
                  <Wrench size={24} className="text-[#1E3A5F]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold text-[#1E3A5F] truncate">
                      {chantier.name || chantier.title || 'Chantier'}
                    </p>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${status.color}`}>
                      {status.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 truncate">
                    {chantier.client_name || chantier.client || 'Client'}
                  </p>
                  {chantier.address && (
                    <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                      <MapPin size={12} />
                      {chantier.address}
                    </p>
                  )}
                </div>
                {chantier.progress !== undefined && (
                  <div className="text-right">
                    <p className="text-sm font-bold text-[#F97316]">{chantier.progress}%</p>
                    <div className="w-16 h-1.5 bg-gray-200 rounded-full mt-1">
                      <div 
                        className="h-full bg-[#F97316] rounded-full" 
                        style={{ width: `${chantier.progress}%` }} 
                      />
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PRODUITS / CATALOGUE
// ═══════════════════════════════════════════════════════════════════════════════
export function ArtifactProductsList({ data, onSelectItem, onRefresh }) {
  const items = Array.isArray(data) ? data : data?.items || data?.products || [];

  const formatMoney = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  if (!items || items.length === 0) {
    return (
      <EmptyState
        icon={Package}
        title="Aucun produit"
        description="Ton catalogue est vide. Ajoute tes produits et services !"
        actionLabel="Ajouter un produit"
        onAction={() => toast.info('Demande-moi d\'ajouter un produit dans le chat !')}
      />
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ListHeader 
        title="Mon Catalogue" 
        count={items.length}
        onRefresh={onRefresh}
        addLabel="Ajouter"
      />
      <div className="flex-1 overflow-y-auto scrollbar-hide grid grid-cols-2 gap-3 p-4">
        {items.map((product, idx) => (
          <motion.div
            key={product.id || product._id || idx}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: idx * 0.05 }}
            className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-md cursor-pointer transition-all"
            onClick={() => onSelectItem?.(product)}
          >
            {product.image && (
              <div className="aspect-square bg-gray-100">
                <img src={product.image} alt="" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-3">
              <p className="font-medium text-[#1E3A5F] text-sm truncate">{product.name || product.title}</p>
              <p className="text-[#F97316] font-bold mt-1">{formatMoney(product.price)}</p>
              {product.stock !== undefined && (
                <p className={`text-xs mt-1 ${product.stock > 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {product.stock > 0 ? `${product.stock} en stock` : 'Rupture'}
                </p>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// NOTES
// ═══════════════════════════════════════════════════════════════════════════════
export function ArtifactNotesList({ data, onSelectItem, onRefresh }) {
  const items = Array.isArray(data) ? data : data?.items || data?.notes || [];

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (!items || items.length === 0) {
    return (
      <EmptyState
        icon={FileText}
        title="Aucune note"
        description="Tu n'as pas encore de notes. Crée-en une pour ne rien oublier !"
        actionLabel="Créer une note"
        onAction={() => toast.info('Demande-moi de créer une note dans le chat !')}
      />
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ListHeader 
        title="Mes Notes" 
        count={items.length}
        onRefresh={onRefresh}
      />
      <div className="flex-1 overflow-y-auto scrollbar-hide divide-y divide-gray-100">
        {items.map((note, idx) => (
          <motion.div
            key={note.id || note._id || idx}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
            className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
            onClick={() => onSelectItem?.(note)}
          >
            <p className="font-semibold text-[#1E3A5F]">{note.title || 'Note sans titre'}</p>
            {note.content && (
              <p className="text-sm text-gray-500 mt-1 line-clamp-2">{note.content}</p>
            )}
            <p className="text-xs text-gray-400 mt-2">{formatDate(note.updated_at || note.created_at)}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONTACTS (Cockpit Particulier) - Liste avec vue détaillée
// ═══════════════════════════════════════════════════════════════════════════════
export function ArtifactContactsList({ 
  data, 
  onSelectItem, 
  onRefresh,
  onStartConversation,
  onOpenConversation,
  onNavigate 
}) {
  const items = Array.isArray(data) ? data : data?.contacts || data?.items || [];
  const [selectedContact, setSelectedContact] = useState(null);

  // Si un contact est sélectionné, afficher la vue détaillée
  if (selectedContact) {
    return (
      <EntityDetailView
        entity={selectedContact}
        entityType="contact"
        onBack={() => setSelectedContact(null)}
        onStartConversation={(contact) => {
          onStartConversation?.(contact, 'contact');
        }}
        onOpenConversation={onOpenConversation}
      />
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-8 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mb-4">
          <Users size={28} className="text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-[#1E3A5F] mb-2">Aucun contact</h3>
        <p className="text-gray-500 text-sm mb-4">
          Les contacts apparaissent quand vous vous suivez mutuellement
        </p>
        <button
          onClick={() => onNavigate?.('/reseau')}
          className="px-4 py-2 bg-[#1E3A5F] text-white text-sm rounded-lg hover:bg-[#2d5a8a] transition-colors"
        >
          Explorer le réseau
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-[#1E3A5F]">Mes Contacts</h2>
            <p className="text-sm text-gray-500">{items.length} contact{items.length > 1 ? 's' : ''}</p>
          </div>
          <button
            onClick={onRefresh}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Actualiser"
          >
            <RefreshCw size={18} className="text-gray-400" />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {items.map((contact, idx) => (
          <motion.div
            key={contact.id || contact._id || idx}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
            onClick={() => setSelectedContact(contact)}
            className="flex items-center gap-3 p-3 bg-white hover:bg-gray-50 rounded-xl border border-gray-100 hover:border-green-200 cursor-pointer transition-all duration-200 group"
            data-testid={`contact-item-${idx}`}
          >
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white font-bold overflow-hidden flex-shrink-0">
              {contact.profile_photo ? (
                <img src={contact.profile_photo} alt="" className="w-full h-full object-cover" />
              ) : (
                (contact.display_name || contact.name || contact.username || '?').charAt(0).toUpperCase()
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-[#1E3A5F] truncate">
                {contact.display_name || contact.name || contact.username || 'Contact'}
              </h3>
              {contact.metier && (
                <p className="text-sm text-gray-500 truncate">{contact.metier}</p>
              )}
              {contact.bio && (
                <p className="text-xs text-gray-400 truncate mt-0.5">{contact.bio}</p>
              )}
            </div>
            <ChevronRight size={20} className="text-gray-400 flex-shrink-0" />
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DASHBOARD GÉNÉRIQUE
// ═══════════════════════════════════════════════════════════════════════════════
export function ArtifactDashboard({ data, title, stats }) {
  const defaultStats = [
    { label: 'Total', value: data?.total || 0, icon: Target, color: 'orange' },
    { label: 'Ce mois', value: data?.this_month || 0, icon: Calendar, color: 'blue' },
    { label: 'En attente', value: data?.pending || 0, icon: Clock, color: 'yellow' },
    { label: 'Terminé', value: data?.completed || 0, icon: CheckCircle, color: 'green' },
  ];

  const displayStats = stats || defaultStats;

  return (
    <div className="h-full overflow-y-auto scrollbar-hide p-5">
      <h2 className="text-lg font-bold text-[#1E3A5F] mb-4">{title || 'Tableau de bord'}</h2>
      
      <div className="grid grid-cols-2 gap-4 mb-6">
        {displayStats.map((stat, idx) => {
          const Icon = stat.icon;
          const colorClasses = {
            orange: 'bg-[#FFF8F4] text-[#F97316]',
            blue: 'bg-[#F0F4F8] text-[#1E3A5F]',
            green: 'bg-green-50 text-green-600',
            yellow: 'bg-yellow-50 text-yellow-600',
            red: 'bg-red-50 text-red-600',
          };
          
          return (
            <div key={idx} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500">{stat.label}</span>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorClasses[stat.color] || colorClasses.blue}`}>
                  <Icon size={16} />
                </div>
              </div>
              <p className="text-2xl font-bold text-[#1E3A5F]">{stat.value}</p>
            </div>
          );
        })}
      </div>

      {data?.recent && data.recent.length > 0 && (
        <div>
          <h3 className="font-semibold text-[#1E3A5F] mb-3">Activité récente</h3>
          <div className="space-y-2">
            {data.recent.slice(0, 5).map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-2 h-2 rounded-full bg-[#F97316]" />
                <p className="text-sm text-gray-700 flex-1">{item.title || item.description}</p>
                <span className="text-xs text-gray-400">{item.date || item.time}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default {
  EmptyState,
  ListHeader,
  EntityDetailView,
  ArtifactDevisList,
  ArtifactFacturesList,
  ArtifactClientsList,
  ArtifactProspectsList,
  ArtifactContactsList,
  ArtifactTasksList,
  ArtifactTeamList,
  ArtifactEventsList,
  ArtifactMessagesList,
  ArtifactChantiersList,
  ArtifactProductsList,
  ArtifactNotesList,
  ArtifactDashboard,
};
